import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import { Button, Tooltip } from '@mui/material';
import { Navigate } from 'react-router-dom';

import TrialsList from '../components/trial/TrialsList';
import DownloadDialog from '../components/DownloadDialog';
import EvaluationDialog, { ExtendedEnrollment } from './EvaluationDialog';
import TrialSearch from '../components/trial/TrialSearch';
import EvaluationResult from '../components/digitalTrial/EvaluationResult';
import { downloadFromContainer } from '../utilities/urlHelper';
import ApiClient from '../loopback/apiClient';
import { ZerpaTableStateSetters } from '../components/layout/table/ZerpaTable';

import {
  showNotification,
  getOverallEvaluation,
  redirectToLogin,
  fetchTrialTags,
  setLoading,
} from '../redux/actions';
import {
  Certificate,
  Enrollment,
  RootState,
  Trial,
  ThirdPartyTrial,
  DetailedEvaluation,
  UserCertificateRequest,
  ZerpaState,
  DigitalSurvey,
  DigitalSubmissionMeta,
  DigitalSection,
  AggregatedSubmission,
} from '../redux/types';
import { LoopbackFilter } from '../loopback';
import TrialTypeTabs from '../components/trial/TrialTypeTabs';
import GenericDialog from '../components/GenericDialog';
import ContentBox from '../components/layout/ContentBox';
import DigitalEvaluationDialog from './DigitalEvaluationDialog';
import withRouter, { RouteType } from '../hoc/withRouter';
import { AuswertungSetting } from '../redux/types/auswertungSettingTypes';

type AlteredEnrollment = UserCertificateRequest['enrollment'] & {
  certificate: UserCertificateRequest;
}

type State = {
  certificate?: Certificate;
  certificates?: UserCertificateRequest[];
  originalCertificates?: UserCertificateRequest[];
  detailedEvaluation?: DetailedEvaluation;
  query?: string;
  year: string;
  tags?: number[];
  selectedTab: 'institute' | 'individual';
  digitalSubmissionMeta?: DigitalSubmissionMeta[];
  digitalSection?: DigitalSection[];
  showDigitalEvaluation?: {
    trialId: number,
    trialName: string,
    enrollmentId: number,
    aggregatedSubmission?: AggregatedSubmission,
    digitalGroundTruthGroupId?: number,
  };
  auswertungSetting?: AuswertungSetting,
}

class CertificatesView extends Component<Props, State> {
  static createEnrollmentObject = (certificate: UserCertificateRequest): AlteredEnrollment => {
    const { enrollment } = certificate;
    const alteredEnrollment: AlteredEnrollment = {
      ...enrollment,
      certificate,
    };
    return alteredEnrollment;
  };

  static requireDeactivateCertificcateButton = (trial: Trial & { certificate: Certificate }) => {
    // NEM 2020 trials. Certificates are issued manually
    // https://smartinmedia.atlassian.net/browse/QZ-447
    if (trial.id === 97 || trial.id === 98 || trial.id === 99 || trial.id === 100) {
      return true;
    }

    // disable only if the trial is deactivated, and certificate is not approved
    if (!trial.certificate.approved && trial.deactivate) {
      return true;
    }

    return false;
  };

  static drtTrialCertDeactivation = (trial: Trial & { certificate: Certificate }) => {
    if (!trial.isDRT) {
      return false;
    }

    if (trial.certificate.evaluation.success === false) {
      return true;
    }

    if (!trial.certificate.certificateTemplateId) {
      return true;
    }

    return false;
  };

  zerpaTableSetter?: ZerpaTableStateSetters;

  static renderTrialStatus = (trial: Trial & { enrollment: Enrollment }) => {
    // todo: status is only as in enrollment.status, where does new string keys come from
    const { status }: { status: string } = trial.enrollment;
    switch (status) {
      case 'available':
        return <FormattedMessage id="certificatesView.certificateAvailable" />;
      case 'no_submission':
        return <FormattedMessage id="certificatesView.submissionMissing" />;
      case 'no_groundtruth':
        return <FormattedMessage id="certificatesView.groundTruthMissing" />;
      default:
        return <FormattedMessage id="certificatesView.unknownState" />;
    }
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      certificate: undefined,
      detailedEvaluation: undefined,
      query: '',
      tags: [],
      year: moment().utc().format('YYYY'),
      selectedTab: 'institute',
      showDigitalEvaluation: undefined,
    };
  }

  componentDidMount() {
    const { fetchTrialTags: fetchTrialTagsAction, trialTags } = this.props;
    this.fetchUserCertificates();
    if (!trialTags) {
      fetchTrialTagsAction();
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { userId } = this.props;
    const { userId: prevUserId } = prevProps;
    const {
      query,
      tags,
      year,
      selectedTab,
    } = this.state;
    const {
      query: pQuery, tags: pTags, year: pYear, selectedTab: pSelectedTab,
    } = prevState;
    const setPageTo0 = userId !== prevUserId
      || query !== pQuery
      || year !== pYear
      || tags?.length !== pTags?.length;

    if (userId !== prevUserId || query !== pQuery || year !== pYear
      || selectedTab !== pSelectedTab) {
      this.fetchUserCertificates();
    }
    if (tags?.length !== pTags?.length) {
      this.filterTrialsWithTags();
    }
    if (this.zerpaTableSetter?.setPage && setPageTo0) {
      this.zerpaTableSetter?.setPage(0);
    }
  }

  onTabChange = (tabId: 'institute' | 'individual') => {
    this.setState({ selectedTab: tabId });
  };

  filterTrialsWithTags = () => {
    const { tags, originalCertificates } = this.state;
    if (tags?.length === 0) {
      this.fetchUserCertificates();
      return;
    }
    const filteredCertificates = originalCertificates?.filter((certificate) => {
      const { enrollment } = certificate;
      const { trial } = enrollment;
      const { trialTagMapping } = trial;
      if (!trialTagMapping || trialTagMapping.length === 0) {
        return false;
      }
      const tagIds = trialTagMapping.map((tag) => tag.tagId);
      return tags?.find((tag) => tagIds.includes(tag));
    });
    this.setState({ certificates: filteredCertificates });
  };

  fetchUserCertificates = async () => {
    const {
      userId,
      instituteId,
      router,
      setLoading: dispatchSetLoading,
    } = this.props;
    const {
      query,
      year,
      selectedTab,
    } = this.state;
    if (!userId) {
      redirectToLogin(router.navigate);
      return;
    }

    if (!instituteId && selectedTab === 'institute') {
      return;
    }

    const filter: LoopbackFilter = {
      where: {
        isDRT: selectedTab === 'individual',
        startDate: {
          between: [
            moment().year(Number(year)).startOf('year'),
            moment().year(Number(year)).endOf('year'),
          ],
        },
      },
    };
    dispatchSetLoading(true);
    const certificates = await ApiClient.getUserCertificates(userId, filter, query)
      .finally(() => dispatchSetLoading(false));
    this.setState({ certificates, originalCertificates: certificates });
  };

  onSearch = ({ searchQuery, year, tags }: {
    searchQuery: string,
    tags: number[],
    year: string
  }) => {
    const { year: stateYear } = this.state;
    this.setState({ query: searchQuery, year: year || stateYear, tags });
  };

  getTooltipText = (): string => {
    const { intl } = this.props;
    return intl.formatMessage({ id: 'certificatesView.blockCertificateMessage' });
  };

  createCurrentTrialsList = () => {
    const { certificates } = this.state;
    if (!certificates) {
      return [];
    }
    return certificates.map(
      (certificate) => ({
        ...certificate.enrollment.trial,
        enroller: `${certificate.enrollment.quipUser.firstname} ${certificate.enrollment.quipUser.surname}`,
        certificate,
        enrollment: CertificatesView.createEnrollmentObject(certificate),
      }),
    );
  };

  onShowDetailedEvaluation = async (trial: Trial & {
    digitalSurveys?: DigitalSurvey[],
    certificate: Certificate,
    enrollment: ExtendedEnrollment,
    thirdPartyTrial: ThirdPartyTrial,
    globalEvaluationReport: any,
  }) => {
    if (trial.isDRT) {
      const { digitalSurveys } = trial;
      const mainSurvey = digitalSurveys?.find((ds) => !ds.exercise);
      if (!mainSurvey) {
        return;
      }
      const extendedSurvey = await ApiClient.fetchDigitalSurvey(mainSurvey.id);
      const digitalSubmissionMeta = await ApiClient.getDigitalSubmissionMeta(trial.id);
      const digitalSection = extendedSurvey.digitalSections
        .filter((ds) => digitalSubmissionMeta.find((dsm) => dsm.selectedSectionId === ds.id));
      // set states
      this.setState({
        digitalSubmissionMeta,
        digitalSection,
      });
    } else if (trial.globalEvaluationReport) {
      downloadFromContainer('globalEvaluationReports', trial.globalEvaluationReport.filename);
    } else if (trial.version && trial.version > 1) {
      this.setState({
        showDigitalEvaluation: {
          trialId: trial.id,
          trialName: trial.name,
          enrollmentId: trial.enrollment.id,
        },
      });

      if (!trial.enrollment.digitalGroundTruthGroupId) {
        return;
      }

      ApiClient.getAggregatedSubmissions(trial.id, trial.enrollment.digitalGroundTruthGroupId)
        .then((aggSub) => this.setState({
          showDigitalEvaluation: {
            aggregatedSubmission: aggSub,
            trialId: trial.id,
            trialName: trial.name,
            enrollmentId: trial.enrollment.id,
            digitalGroundTruthGroupId: trial.enrollment.digitalGroundTruthGroupId || undefined,
          },
        }));
    } else {
      const detailedEval = await ApiClient.getDetailedEvaluation(trial.enrollment.id);
      this.setState({ detailedEvaluation: detailedEval });
    }
  };

  renderDownloadCertificate = (
    trial: Trial & {
      certificate: Certificate,
      enrollment: ExtendedEnrollment,
      thirdPartyTrial: ThirdPartyTrial,
      globalEvaluationReport: any,
    },
  ) => {
    const { auswertungSetting } = this.state;
    if (!trial) {
      return <div />;
    }
    if (auswertungSetting === undefined || auswertungSetting.trialId !== trial.id) {
      ApiClient.getAuswertungSetting(Number(trial.id))
        .then((s) => s && this.setState({ auswertungSetting: s }));
    }
    const { detailedEvaluation } = this.state;
    const deactivateCertificateButton = CertificatesView.requireDeactivateCertificcateButton(trial)
      || CertificatesView.drtTrialCertDeactivation(trial);
    return (
      <div>
        <Tooltip title={deactivateCertificateButton ? this.getTooltipText() : ''}>
          <Button
            variant="contained"
            id={`DownloadCertificate-${trial.code}`}
            className="mr2 mb2"
            onClick={() => this.setState({ certificate: trial.certificate })}
            color="primary"
            disabled={deactivateCertificateButton}
          >
            <FormattedMessage id="certificatesView.getCertificate" />
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          id={`DetailedEvaluation-${trial.code}`}
          className="mr2 mb2"
          onClick={() => this.onShowDetailedEvaluation(trial)}
          disabled={!!trial.thirdPartyTrial || !trial.certificate.approved}
          color="primary"
        >
          <FormattedMessage id="enrollmentsmanager.detailedEvaluationButton" />
        </Button>
        {
          detailedEvaluation && (
            <EvaluationDialog
              enrollment={trial.enrollment}
              trial={trial}
              evaluation={detailedEvaluation}
              onClose={() => this.setState({ detailedEvaluation: undefined })}
              auswertungSetting={auswertungSetting}
            />
          )
        }
      </div>
    );
  };

  onZerpaTableStateChange = (
    _zerpaState: ZerpaState,
    stateSetters: ZerpaTableStateSetters,
  ) => {
    if (!this.zerpaTableSetter) {
      this.zerpaTableSetter = stateSetters;
    }
  };

  render() {
    const {
      userId,
      trialTags,
    } = this.props;
    const {
      certificate,
      selectedTab,
      digitalSubmissionMeta,
      digitalSection,
      showDigitalEvaluation,
      auswertungSetting,
    } = this.state;
    if (!userId) {
      <Navigate to="/login" />;
    }
    return (
      <>
        <div>
          <ContentBox
            elevation={0}
            title={<FormattedMessage id="certificatesView.acquiredCertificates" />}
          >
            <TrialTypeTabs
              onTabChange={this.onTabChange}
              selectedTab={selectedTab}
            />
            <TrialSearch
              data={this.state}
              onSearch={this.onSearch}
              trialTags={trialTags}
            />
            <TrialsList
              trials={this.createCurrentTrialsList()}
              renderTrialStatus={() => <FormattedMessage id="certificatesView.certificateIssued" />}
              renderTrialDetails={false}
              renderTrialActions={this.renderDownloadCertificate}
              onZerpaTableStateChange={this.onZerpaTableStateChange}
              showDeactivatedTrials
              maintainRowColor
              selectedTab={selectedTab}
            />
          </ContentBox>
          {
            certificate && (
              <DownloadDialog
                isOpen={!!certificate}
                onClose={() => this.setState({ certificate: undefined })}
                container={certificate.container}
                file={certificate.file}
                id={certificate.id}
              />
            )
          }
        </div>
        {
          digitalSubmissionMeta && digitalSection && (
            <GenericDialog
              isOpen={!!digitalSubmissionMeta && !!digitalSection}
              onRequestClose={() => this.setState({
                digitalSubmissionMeta: undefined,
                digitalSection: undefined,
              })}
            >
              {/*
                TODO: here we need to use a different component in place of EvaluationResult
                which will accept array of digital submission meta and digital section
                in case of multiple digital sections
              */}
              <EvaluationResult
                digitalSection={digitalSection[0]}
                digitalSubmissionMeta={digitalSubmissionMeta[0]}
                hideCertificateButton
              />
            </GenericDialog>
          )
        }
        {
          showDigitalEvaluation && (
            <DigitalEvaluationDialog
              trialId={showDigitalEvaluation.trialId}
              trialName={showDigitalEvaluation.trialName}
              enrollmentId={showDigitalEvaluation.enrollmentId}
              aggregatedSubmission={showDigitalEvaluation.aggregatedSubmission}
              onClose={() => this.setState({ showDigitalEvaluation: undefined })}
              groundTruthGroupId={showDigitalEvaluation.digitalGroundTruthGroupId}
              auswertungSetting={auswertungSetting}
            />
          )
        }
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userId: state.users && state.users.token && state.users.token.userId,
  instituteId: state.users.token?.institute?.id,
  locale: state.intl.locale,
  trialTags: state.trialTag.byId,
});

const dispatchProps = {
  showNotification,
  getOverallEvaluation,
  fetchTrialTags,
  setLoading,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = {
  intl: {
    formatMessage: (arg: Record<string, unknown>) => string;
  }
} & PropsFromRedux & RouteType;

export default injectIntl(connector(withRouter(CertificatesView)));
